/**
 * Registers Election.sol revert string → Persian message mapping with
 * the existing handleContractError cascade (useContractError.ts §registerContractErrorHandler).
 *
 * Import this module once at the DAO route entry point before any vote/withdraw
 * mutation can run. The handler short-circuits before the UNKNOWN fallback — every
 * Election.sol revert string produces a specific Persian message (AC-INV-5, D5).
 *
 * See: arandao-docs/pipeline/DAO-FOUND-02/idocs/voting/service-logic.md §2.4
 */
import type { useTranslations } from "next-intl";
import type { ContractFunctionRevertedError } from "viem";
import { registerContractErrorHandler } from "@/hooks/useContractError";

/**
 * Translator bound to the `dao` message namespace. The Persian/en/ar error
 * copy lives under `dao.voting.errors.*`; the caller (DaoTabsNav, a client
 * component) builds this via `useTranslations("dao")` and passes it in so this
 * non-hook module never imports next-intl at module scope.
 */
type DaoT = ReturnType<typeof useTranslations<"dao">>;

let registered = false;

function extractRevertMessage(error: ContractFunctionRevertedError): string {
  if (error.reason) return error.reason;
  if (error.shortMessage) return error.shortMessage;
  return error.message;
}

export function registerElectionErrors(t: DaoT): void {
  if (registered) return;
  registered = true;

  registerContractErrorHandler((error) => {
    const msg = extractRevertMessage(error);

    if (msg.includes("you cannot vote for yourself"))
      return t("voting.errors.voteForYourself");
    if (msg.includes("Invalid value!")) return t("voting.errors.invalidValue");
    if (msg.includes("invalid candidate"))
      return t("voting.errors.invalidCandidate");
    if (msg.includes("invalid allowance"))
      return t("voting.errors.invalidAllowance");
    if (msg.includes("we are not in the voting period"))
      return t("voting.errors.notVotingPeriod");
    if (msg.includes("we are in the voting period"))
      return t("voting.errors.stillVotingPeriod");
    if (msg.includes("you have no dnm")) return t("voting.errors.noDnm");
    if (msg.includes("Insufficient amount of dnm"))
      return t("voting.errors.insufficientDnm");
    if (msg.includes("invalid amount")) return t("voting.errors.invalidAmount");

    return null; // fall through to next handler / cascade
  });
}
